import React, { useState } from "react";
import styled from "styled-components";

import type { Shot } from "../../../../app/services/shots";
import ToolTip from "../../../../components/tooltip/ToolTip";

const STAR_ACTIVE_COLOR = "#FFF000";
const STAR_INACTIVE_COLOR = "#d0d0d0";

const StarsRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0;
`;

/** クライアント向け: セル内で直接クリックして評価する星ボタン */
const StarButton = styled.button<{ $on: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  line-height: 1;

  .material-symbols-rounded {
    font-size: 16px;
    line-height: 1;
    color: ${({ $on }) => ($on ? STAR_ACTIVE_COLOR : STAR_INACTIVE_COLOR)};
    transition: color 0.12s ease;
  }
`;

/** 表示専用の星（平均値など） */
const StaticStar = styled.span.attrs({
  className: "material-symbols-rounded",
})<{ $on: boolean }>`
  font-size: 14px;
  line-height: 1;
  color: ${({ $on }) => ($on ? STAR_ACTIVE_COLOR : STAR_INACTIVE_COLOR)};
`;

const StaticStars: React.FC<{ rating: number }> = ({ rating }) => {
  const rounded = Math.round(rating * 2) / 2;
  const full = Math.floor(rounded);
  const hasHalf = rounded % 1 !== 0;
  return (
    <StarsRow aria-label={`評価 ${rating.toFixed(1)}`}>
      {[1, 2, 3, 4, 5].map((star) => {
        if (star <= full)
          return (
            <StaticStar key={star} $on>
              star
            </StaticStar>
          );
        if (hasHalf && star === full + 1)
          return (
            <StaticStar key={star} $on>
              star_half
            </StaticStar>
          );
        return (
          <StaticStar key={star} $on={false}>
            star
          </StaticStar>
        );
      })}
    </StarsRow>
  );
};

interface RatingCellProps {
  shot: Shot;
  /** 自分の評価を入力できるか（クライアント系ロール & can_rate） */
  canRate: boolean;
  /** クライアント系ロールなら自分の評価、プロデューサー系なら全体平均を表示する */
  isClientGroup: boolean;
  onRate: (shot: Shot, rating: number) => void;
  onClear: (shot: Shot) => void;
}

/**
 * ショット一覧テーブルの評価セル。
 *
 * - クライアント（can_rate）: セル内の星を直接クリックして評価。
 *   現在と同じ星をクリックすると評価を取り消す。
 * - プロデューサー系: 全体平均と件数の表示のみ（入力不可）。
 */
const RatingCell: React.FC<RatingCellProps> = ({
  shot,
  canRate,
  isClientGroup,
  onRate,
  onClear,
}) => {
  const [hovered, setHovered] = useState(0);

  if (canRate) {
    const myRating = shot.my_rating ?? 0;
    const display = hovered > 0 ? hovered : myRating;
    return (
      <StarsRow onMouseLeave={() => setHovered(0)}>
        {[1, 2, 3, 4, 5].map((star) => (
          <ToolTip
            key={star}
            description={
              star === myRating ? "クリックで評価を取り消す" : `${star} で評価`
            }
          >
            <StarButton
              $on={star <= display}
              aria-label={
                star === myRating ? "評価を取り消す" : `${star} で評価`
              }
              onMouseEnter={() => setHovered(star)}
              onClick={() =>
                star === myRating ? onClear(shot) : onRate(shot, star)
              }
            >
              <span className="material-symbols-rounded">star</span>
            </StarButton>
          </ToolTip>
        ))}
      </StarsRow>
    );
  }

  const value = isClientGroup ? shot.my_rating : shot.average_rating;
  if (!value || value <= 0) {
    return null;
  }

  const count = shot.ratings ? shot.ratings.length : 0;
  return (
    <div className="u-row u-items-center u-gap4">
      <StaticStars rating={value} />
      {!isClientGroup && count > 0 && (
        <span className="bodytext-11 u-text-light-gray">
          {value.toFixed(1)}（{count}）
        </span>
      )}
    </div>
  );
};

export default RatingCell;
